import React, { useEffect, useMemo, useRef, useState } from "react";
import LeaseUpCalculator from "./LeaseUpCalculator";
import { useCompareHover } from "../modals/CompareHoverContext";

// ---------------- helpers (cursor-safe formatters) ----------------
const formatMoney = (v, maxFrac = 2) => {
  const s = String(v ?? "");
  const cleaned = s.replace(/[$,\s]/g, "");
  if (cleaned === "" || cleaned === "-") return cleaned;

  const neg = cleaned.startsWith("-");
  let body = neg ? cleaned.slice(1) : cleaned;
  const endsWithDot = /\.\s*$/.test(cleaned);
  const firstDot = body.indexOf(".");
  const hasDot = firstDot !== -1;

  if (hasDot) {
    body = body.slice(0, firstDot + 1) + body.slice(firstDot + 1).replace(/\./g, "");
  }

  const [intRaw, fracRawAll] = body.split(".");
  const intNum = intRaw ? Number(intRaw) : 0;
  const intFmt = Number.isFinite(intNum) ? intNum.toLocaleString() : intRaw;

  if (maxFrac === 0) return `$${neg ? "-" : ""}${intFmt}`;

  let out = `$${neg ? "-" : ""}${intFmt}`;
  if (hasDot) {
    out += ".";
    if (!endsWithDot) out += (fracRawAll || "").slice(0, maxFrac);
  }
  return out;
};
const $0 = (v) => formatMoney(v, 0);
const $2 = (v) => formatMoney(v, 2);

const formatPercent = (v, maxFrac = 3) => {
  const s = String(v ?? "");
  const cleaned = s.replace(/[%\s,]/g, "");
  if (cleaned === "" || cleaned === "-") return cleaned;

  const neg = cleaned.startsWith("-");
  let body = neg ? cleaned.slice(1) : cleaned;
  const endsWithDot = /\.\s*$/.test(cleaned);
  const firstDot = body.indexOf(".");
  const hasDot = firstDot !== -1;

  if (hasDot) {
    body = body.slice(0, firstDot + 1) + body.slice(firstDot + 1).replace(/\./g, "");
  }
  const [intRaw, fracRawAll] = body.split(".");
  const intNum = intRaw ? Number(intRaw) : 0;
  const intFmt = Number.isFinite(intNum) ? intNum.toLocaleString() : intRaw;

  let out = `${neg ? "-" : ""}${intFmt}`;
  if (hasDot) {
    out += ".";
    if (!endsWithDot) out += (fracRawAll || "").slice(0, maxFrac);
  }
  return `${out}%`;
};

const n = (v) => {
  if (v === "" || v == null || v === "-") return 0;
  const s = String(v).replace(/[$,%\s,]/g, "");
  const num = Number(s);
  return Number.isFinite(num) ? num : 0;
};
// accepts "8%" or "0.08" -> 0.08
const nPct = (v) => {
  if (v == null || v === "") return 0;
  const str = String(v).trim();
  const num = n(v);
  return str.endsWith("%") ? num / 100 : num;
};

export default function IncomeBasic({ data, onChange, onSave, packet, dataKey = "income" }) {
  // ---------------- pull context from Cover ----------------
  const coverCtx = useMemo(() => {
    let latest = null;
    let maxOrder = -Infinity;
    for (const s of packet?.sections || []) {
      if (String(s?.type || "").toLowerCase() === "cover") {
        const oi = s?.order_index ?? 0;
        if (oi > maxOrder) { maxOrder = oi; latest = s; }
      }
    }
    const pi = latest?.data?.property_info || {};
    const unitType = pi.unit_type || "units"; // "units" | "SF" | "beds"
    const unitsOrSF = Number(String(pi.units ?? "").replace(/[$,%\s,]/g, "")) || 0;
    return {
      unitType,
      basisFromCover: unitsOrSF, // Units OR Area(SF) depending on unitType
    };
  }, [packet]);

  // ---------------- form init (BASIC) ----------------
  const initial = (data && typeof data === "object" ? data[dataKey] : undefined) || {};
  const [form, setForm] = useState({
    // BASIC modes
    pgi_mode: initial.pgi_mode === "total" || initial.pgi_mode === "per_unit"
      ? initial.pgi_mode
      : (initial.pgi_per_unit ? "per_unit" : "total"),
    vac_mode: String(initial.vacancy || "").trim().endsWith("%") ? "pct" : "dollar",
    exp_mode: initial.exp_mode === "pct_egi" ? "pct_egi" : "total",

    // BASIC values (formatted)
    pgi_total: $0(initial.pgi_total ?? initial.potential_gross ?? ""),
    pgi_per_unit: $2(initial.pgi_per_unit ?? ""),
    vacancy: String(initial.vacancy || "").trim().endsWith("%")
      ? formatPercent(initial.vacancy)
      : $0(initial.vacancy ?? ""),
    expenses_total: $0(initial.expenses_total ?? initial.expenses ?? ""),
    expenses_pct: formatPercent(initial.expenses_pct ?? ""),

    // Cap / deductions / notes
    cap_mode: initial.cap_mode === "loaded" ? "loaded" : "unloaded",
    base_cap_rate: formatPercent(initial.base_cap_rate ?? initial.cap_rate ?? ""),
    effective_tax_rate: formatPercent(initial.effective_tax_rate ?? ""),

    deduction_type: initial.deduction_type || "none",
    deduction_amount: $0(initial.deduction_amount ?? ""),
    deduction_notes: initial.deduction_notes || "",
    lease_up: initial.lease_up || null,

    notes: initial.notes || "",
  });

  // --- guard to avoid re-syncing the same payload back onto form mid-typing ---
  const lastUpstreamJSON = useRef(JSON.stringify(initial));

  useEffect(() => {
    const inc = (data && typeof data === "object" ? data[dataKey] : undefined);
    if (!inc) return;
    const pjson = JSON.stringify(inc);
    if (pjson === lastUpstreamJSON.current) return; // ignore our own fresh push

    setForm((prev) => ({
      ...prev,
      pgi_mode: inc.pgi_mode || prev.pgi_mode,
      vac_mode: String(inc.vacancy || "").trim().endsWith("%") ? "pct" : "dollar",
      exp_mode: inc.exp_mode === "pct_egi" ? "pct_egi" : "total",

      pgi_total: $0(inc.pgi_total ?? inc.potential_gross ?? prev.pgi_total),
      pgi_per_unit: $2(inc.pgi_per_unit ?? prev.pgi_per_unit),
      vacancy: String(inc.vacancy || "").trim().endsWith("%") ? formatPercent(inc.vacancy) : $0(inc.vacancy ?? prev.vacancy),
      expenses_total: $0(inc.expenses_total ?? inc.expenses ?? prev.expenses_total),
      expenses_pct: formatPercent(inc.expenses_pct ?? prev.expenses_pct),

      cap_mode: inc.cap_mode === "loaded" ? "loaded" : "unloaded",
      base_cap_rate: formatPercent(inc.base_cap_rate ?? inc.cap_rate ?? prev.base_cap_rate),
      effective_tax_rate: formatPercent(inc.effective_tax_rate ?? prev.effective_tax_rate),

      deduction_type: inc.deduction_type ?? prev.deduction_type,
      deduction_amount: $0(inc.deduction_amount ?? prev.deduction_amount),
      deduction_notes: inc.deduction_notes ?? prev.deduction_notes,
      lease_up: inc.lease_up ?? prev.lease_up,

      notes: inc.notes ?? prev.notes,
    }));
    lastUpstreamJSON.current = pjson;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify((data && data[dataKey]) || {})]);

  // ---------------- derived (BASIC) ----------------
  const UNITS = useMemo(() => {
    if (coverCtx.unitType === "SF") return 0;
    return Math.max(0, Math.floor(coverCtx.basisFromCover || 0));
  }, [coverCtx]);

  const AREA_SF = useMemo(() => {
    if (coverCtx.unitType === "SF") return coverCtx.basisFromCover || 0;
    return 0;
  }, [coverCtx]);

  const BASIS = useMemo(() => (coverCtx.unitType === "SF" ? AREA_SF : UNITS), [coverCtx.unitType, AREA_SF, UNITS]);

  const PGI = useMemo(() => {
    if (form.pgi_mode === "per_unit") {
      return BASIS > 0 ? n(form.pgi_per_unit) * BASIS : 0;
    }
    return n(form.pgi_total);
  }, [form.pgi_mode, form.pgi_total, form.pgi_per_unit, BASIS]);

  const vacancyDollarDisplay = useMemo(() => {
    return form.vac_mode === "dollar" ? form.vacancy : $0(PGI * nPct(form.vacancy));
  }, [form.vac_mode, form.vacancy, PGI]);

  const vacancyPctDisplay = useMemo(() => {
    return form.vac_mode === "pct" ? form.vacancy : (PGI > 0 ? formatPercent((n(form.vacancy) / PGI) * 100) : "");
  }, [form.vac_mode, form.vacancy, PGI]);

  const VAC = useMemo(() => (form.vac_mode === "pct" ? PGI * nPct(form.vacancy) : n(form.vacancy)), [form.vac_mode, form.vacancy, PGI]);
  const EGI = useMemo(() => Math.max(0, PGI - VAC), [PGI, VAC]);
  
  // Vacancy as a percent (0..100) for Lease-Up/target occupancy
  const VAC_PCT = useMemo(() => {
    if (form.vac_mode === "pct") {
      // "10%" or "0.10" -> 10
      return Math.max(0, Math.min(100, nPct(form.vacancy) * 100));
    }
    // dollar mode -> convert dollars to % of PGI
    return PGI > 0 ? Math.max(0, Math.min(100, (n(form.vacancy) / PGI) * 100)) : 0;
  }, [form.vac_mode, form.vacancy, PGI]);

  const EXP = useMemo(
    () => (form.exp_mode === "pct_egi" ? Math.max(0, EGI * nPct(form.expenses_pct)) : n(form.expenses_total)),
    [form.exp_mode, form.expenses_total, form.expenses_pct, EGI]
  );

  const NOI = useMemo(() => Math.max(0, EGI - EXP), [EGI, EXP]);

  const BASE_CAP = useMemo(() => nPct(form.base_cap_rate), [form.base_cap_rate]);
  const ETR = useMemo(() => nPct(form.effective_tax_rate), [form.effective_tax_rate]);
  const APPLIED_CAP = useMemo(() => Math.max(0, BASE_CAP + (form.cap_mode === "loaded" ? ETR : 0)), [BASE_CAP, ETR, form.cap_mode]);

  const INDICATED_VALUE = useMemo(() => (APPLIED_CAP > 0 ? NOI / APPLIED_CAP : 0), [NOI, APPLIED_CAP]);

  // keep % of EGI and $ total synchronized
  useEffect(() => {
    if (form.exp_mode === "pct_egi") {
      const totFmt = $0(EGI * nPct(form.expenses_pct));
      if (totFmt !== form.expenses_total) setForm((prev) => ({ ...prev, expenses_total: totFmt }));
    } else if (form.exp_mode === "total") {
      const pctFmt = EGI > 0 ? formatPercent((n(form.expenses_total) / EGI) * 100) : "";
      if (pctFmt !== form.expenses_pct) setForm((prev) => ({ ...prev, expenses_pct: pctFmt }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [EGI]);

  // keep PGI total and per-unit synchronized
  useEffect(() => {
    if (form.pgi_mode === "per_unit") {
      const nextTotal = $0(n(form.pgi_per_unit) * BASIS);
      if (nextTotal !== form.pgi_total) setForm((prev) => ({ ...prev, pgi_total: nextTotal }));
    } else {
      const nextRate = BASIS > 0 ? $2(n(form.pgi_total) / BASIS) : form.pgi_per_unit;
      if (nextRate !== form.pgi_per_unit) setForm((prev) => ({ ...prev, pgi_per_unit: nextRate }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BASIS, form.pgi_mode]);

  // ---------------- push up to PacketBuilder ----------------
  useEffect(() => {
    const prev = (data && data[dataKey]) || {};
    const prevLineItems =
      (prev && typeof prev.line_items === "object" ? prev.line_items : { income: [], vcloss: [], expenses: [] });

    const manualAmount   = n(form.deduction_amount);
	const leaseUpAmount  = Number(form?.lease_up?.deduction) || 0;
	const dt             = form.deduction_type;
	const activeDeduction =
	  dt === "manual"   ? manualAmount :
	  dt === "lease_up" ? leaseUpAmount :
	  0; // none

    const payload = {
      [dataKey]: {
        ui_mode: "basic",

        // Persist only basic fields (keep any Advanced arrays intact)
        pgi_mode: form.pgi_mode,
        vac_mode: form.vac_mode,
        exp_mode: form.exp_mode,
        pgi_total: form.pgi_total,
        pgi_per_unit: form.pgi_per_unit,
        vacancy: form.vacancy,
        expenses_total: form.expenses_total,
        expenses_pct: form.expenses_pct,
        cap_mode: form.cap_mode,
        base_cap_rate: form.base_cap_rate,
        effective_tax_rate: form.effective_tax_rate,
        deduction_type: form.deduction_type,
        deduction_amount: form.deduction_amount,
        deduction_notes: form.deduction_notes,
        lease_up: form.lease_up,
        notes: form.notes,

        line_items: prevLineItems,

        // mirrors
        potential_gross: $0(PGI),
        expenses: $0(EXP),
        noi: $0(NOI),
        cap_rate: formatPercent(APPLIED_CAP * 100),
        indicated_value: $0(INDICATED_VALUE),
        deduction_applied: $0(activeDeduction),
        final_value: $0(Math.max(0, INDICATED_VALUE - activeDeduction)),

        _calc: {
          PGI, VAC, EGI, EXP, NOI, base_cap: BASE_CAP, effective_tax_rate: ETR, applied_cap: APPLIED_CAP,
        },
      },
    };

    onChange?.(payload);
    // ✅ update the guard so the immediate parent echo doesn't re-sync onto us
    lastUpstreamJSON.current = JSON.stringify(payload[dataKey]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify({
      pgi_mode: form.pgi_mode, vac_mode: form.vac_mode, exp_mode: form.exp_mode,
      pgi_total: form.pgi_total, pgi_per_unit: form.pgi_per_unit, vacancy: form.vacancy,
      expenses_total: form.expenses_total, expenses_pct: form.expenses_pct,
      cap_mode: form.cap_mode, base_cap_rate: form.base_cap_rate, effective_tax_rate: form.effective_tax_rate,
      deduction_type: form.deduction_type, deduction_amount: form.deduction_amount, lease_up: form.lease_up,
      notes: form.notes,
    }),
    BASIS, PGI, VAC, EGI, EXP, NOI, BASE_CAP, ETR, APPLIED_CAP, INDICATED_VALUE,
  ]);

  // ---------------- UI (Basic) ----------------
  return (
    <div className="space-y-6">
      {/* Context from Cover */}
      <ContextFromCover unitType={coverCtx.unitType} basis={BASIS} areaSF={AREA_SF} units={UNITS} />

      {/* PGI */}
      <ChoiceRow
        title="Potential Gross Income (PGI)"
        cols={2}
        activeKey={form.pgi_mode}
        onSwitch={(k) => {
          if (k === "per_unit") {
            const rateFmt = $2(form.pgi_per_unit);
            const totalFmt = $0(n(rateFmt) * BASIS);
            setForm((prev) => ({ ...prev, pgi_mode: "per_unit", pgi_per_unit: rateFmt, pgi_total: totalFmt }));
          } else {
            const totalFmt = $0(form.pgi_total);
            const rateFmt = BASIS > 0 ? $2(n(totalFmt) / BASIS) : form.pgi_per_unit;
            setForm((prev) => ({ ...prev, pgi_mode: "total", pgi_total: totalFmt, pgi_per_unit: rateFmt }));
          }
        }}
        items={[
          { key: "total", label: "Total ($)", value: form.pgi_total, onChange: (v) => setForm((p) => ({ ...p, pgi_total: $0(v), pgi_mode: "total" })) },
          {
            key: "per_unit",
            label: coverCtx.unitType === "SF" ? "$/SF" : coverCtx.unitType === "beds" ? "$/Bed" : "$/Unit",
            value: form.pgi_per_unit,
            onChange: (v) => setForm((p) => ({ ...p, pgi_per_unit: $2(v), pgi_mode: "per_unit" })),
            disabled: (coverCtx.unitType === "SF" ? AREA_SF : UNITS) === 0,
            disabledHint: coverCtx.unitType === "SF" ? "Provide Area (SF) on Cover." : "Add Units on the Cover page.",
            inputMode: "decimal",
          },
        ]}
      />

      {/* Vacancy */}
      <ChoiceRow
        title="Vacancy & Credit Loss"
        cols={2}
        activeKey={form.vac_mode}
        onSwitch={(k) =>
          setForm((prev) => {
            if (k === prev.vac_mode) return prev;
            if (k === "pct") {
              const pctFmt = PGI > 0 ? formatPercent((n(prev.vacancy) / PGI) * 100) : prev.vacancy;
              return { ...prev, vac_mode: "pct", vacancy: pctFmt };
            } else {
              const totFmt = $0(PGI * nPct(prev.vacancy));
              return { ...prev, vac_mode: "dollar", vacancy: totFmt };
            }
          })
        }
        items={[
          {
            key: "dollar",
            label: "Total ($)",
            value: vacancyDollarDisplay,
            onChange: (v) => setForm((prev) => ({ ...prev, vac_mode: "dollar", vacancy: $0(v) })),
            placeholder: "e.g., $120,000",
          },
          {
            key: "pct",
            label: "% of PGI",
            value: vacancyPctDisplay,
            onChange: (v) => setForm((prev) => ({ ...prev, vac_mode: "pct", vacancy: formatPercent(v) })),
            placeholder: "e.g., 8%",
            inputMode: "decimal",
          },
        ]}
      />

      {/* EGI (read-only) */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
          label="Effective Gross Income (EGI)"
          value={$0(EGI)}
          onChange={() => {}}
          readOnly
          compareKey="income.egi"
        />
      </div>

      {/* Expenses */}
      <ChoiceRow
        title="Operating Expenses"
        cols={2}
        activeKey={form.exp_mode}
        onSwitch={(k) => {
          setForm((prev) => {
            if (k === "pct_egi") {
              const pctFmt = EGI > 0 ? formatPercent((n(prev.expenses_total) / EGI) * 100) : prev.expenses_pct;
              return { ...prev, exp_mode: "pct_egi", expenses_pct: pctFmt };
            }
            const totFmt = $0(EGI * nPct(prev.expenses_pct));
            return { ...prev, exp_mode: "total", expenses_total: totFmt };
          });
        }}
        items={[
          { key: "total", label: "Total ($)", value: form.expenses_total, onChange: (v) => setForm((p) => ({ ...p, expenses_total: $0(v) })) },
          {
            key: "pct_egi",
            label: "% of EGI",
            value: form.expenses_pct,
            onChange: (v) => setForm((p) => ({ ...p, expenses_pct: formatPercent(v) })),
            placeholder: "e.g., 35%",
            inputMode: "decimal",
          },
        ]}
      />

      {/* NOI (read-only) — NEW, placed just above Cap */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Field
          label="Net Operating Income (NOI)"
          value={$0(NOI)}
          onChange={() => {}}
          readOnly
          compareKey="income.noi"
        />
      </div>

      {/* Cap block */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Cap Mode</label>
          <Toggle
            options={[
              { key: "unloaded", label: "Unloaded" },
              { key: "loaded", label: "Loaded (Base + ETR)" },
            ]}
            value={form.cap_mode}
            onChange={(v) => setForm((prev) => ({ ...prev, cap_mode: v }))}
          />
        </div>
        <Field
          label="Base Cap Rate (%)"
          value={form.base_cap_rate}
          onChange={(v) => setForm((prev) => ({ ...prev, base_cap_rate: formatPercent(v) }))}
          placeholder="e.g., 7.25%"
          inputMode="decimal"
          compareKey="income.cap_rate"
        />
        {form.cap_mode === "loaded" && (
          <Field
            label="Effective Tax Rate (%)"
            value={form.effective_tax_rate}
            onChange={(v) => setForm((prev) => ({ ...prev, effective_tax_rate: formatPercent(v) }))}
            placeholder="e.g., 2.20%"
            inputMode="decimal"
            compareKey="income.cap_rate"
          />
        )}
      </div>

      {/* Deduction */}
      <DeductionBlock
        form={form}
        setForm={setForm}
        PGI={PGI}
        EXP={EXP}
        vacancyPct={VAC_PCT}
        appliedCap={APPLIED_CAP}
        totalBuildingSF={AREA_SF}
      />

      {/* Value panel */}
      <ValuePanel
        deductionType={form.deduction_type}
        indicated={INDICATED_VALUE}
        manualAmount={n(form.deduction_amount)}
        leaseUp={Number(form?.lease_up?.deduction) || 0}
      />

      {/* Notes */}
      <Notes value={form.notes} onChange={(v) => setForm((p) => ({ ...p, notes: v }))} />

      {/* Actions */}
      <div className="flex items-center gap-2">
      </div>
    </div>
  );
}

/* ---------------- small UI primitives (same look & feel) ---------------- */

function Field({
  label,
  value,
  onChange,
  placeholder,
  inputMode = "text",
  readOnly = false,
  compareKey,
}) {
  const hover = useCompareHover();
  const enableHover = !!compareKey && !!hover;

  return (
    <div
      onMouseEnter={(e) => enableHover && hover.enterIncome(e, { key: compareKey, label })}
      onMouseMove={(e) => enableHover && hover.move(e)}
      onMouseLeave={() => enableHover && hover.leave()}
    >
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        value={value ?? ""}
        onChange={(e) => !readOnly && onChange?.(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        readOnly={readOnly}
        className={[
          "w-full rounded-lg border px-3 py-2 outline-none transition",
          readOnly
            ? "bg-slate-50 border-slate-200 text-slate-500"
            : "bg-white border-gray-300 focus:border-slate-400 focus:ring-2 focus:ring-slate-200",
        ].join(" ")}
      />
    </div>
  );
}

function ChoiceRow({ title, items, activeKey, onSwitch, cols = items?.length || 2 }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div className="text-sm font-medium">{title}</div>
        <div className="inline-flex rounded-full border border-slate-300 bg-white overflow-hidden">
          {items.map((it, i) => {
            const active = it.key === activeKey;
            const disabled = !!it.disabled;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => !disabled && onSwitch?.(it.key)}
                className={[
                  "px-3 py-1 text-xs transition",
                  i > 0 ? "border-l border-slate-300" : "",
                  disabled
                    ? "text-slate-400 cursor-not-allowed bg-slate-50"
                    : active
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-700 hover:bg-slate-100",
                ].join(" ")}
                title={disabled ? (it.disabledHint || "Unavailable") : `Use ${it.label}`}
                disabled={disabled}
              >
                {it.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Inputs */}
      <div className={`grid sm:grid-cols-${cols} gap-3`}>
        {items.map((it) => {
          const active = it.key === activeKey;
          return (
            <div key={it.key} className="relative">
              <Field
                label={it.label}
                value={it.value}
                onChange={(v) => active && it.onChange?.(v)}
                placeholder={it.placeholder}
                inputMode={it.inputMode}
                readOnly={!active}
              />
              {!active && it.disabled && (
                <div className="absolute -bottom-5 left-0 text-[11px] text-amber-600">
                  {it.disabledHint}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Toggle({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
      {options.map((opt, i) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange?.(opt.key)}
            className={[
              "px-3 py-1.5 text-sm",
              active ? "bg-slate-800 text-white" : "bg-white text-slate-700",
              i > 0 ? "border-l border-slate-300" : "",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
function Badge({ children }) {
  return <span className="inline-flex items-center rounded-full border border-slate-200 px-2 py-0.5 text-xs">{children}</span>;
}
function ContextFromCover({ unitType, basis, areaSF, units }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm flex flex-wrap items-center gap-3">
      <span className="text-slate-600">Context:</span>
      {unitType === "SF" ? (
        <Badge>
          Area (SF): <strong>{areaSF ? areaSF.toLocaleString() : "—"}</strong>
        </Badge>
      ) : (
        <Badge>
          Units: <strong>{units ? units.toLocaleString() : "—"}</strong>
        </Badge>
      )}
    </div>
  );
}
function DeductionBlock({ form, setForm, PGI, EXP, vacancyPct, appliedCap, totalBuildingSF }) {
  const n = (v) => Number(String(v).replace(/[$,%\s,]/g, "")) || 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
      <div className="text-sm font-medium">Deduction (subtract from capitalized value)</div>
      <Toggle
        options={[
          { key: "none", label: "None" },
          { key: "manual", label: "Manual" },
          { key: "lease_up", label: "Lease-Up (NPV)" },
        ]}
        value={form.deduction_type}
        onChange={(v) => setForm((prev) => ({ ...prev, deduction_type: v }))}
      />
      {form.deduction_type === "manual" && (
        <div className="grid sm:grid-cols-3 gap-3">
          <Field
            label="Description / Notes"
            value={form.deduction_notes}
            onChange={(v) => setForm((prev) => ({ ...prev, deduction_notes: v }))}
            placeholder="e.g., Environmental remediation"
          />
          <Field
            label="Amount ($)"
            value={form.deduction_amount}
            onChange={(v) => setForm((prev) => ({ ...prev, deduction_amount: $0(v) }))}
            placeholder="e.g., 250000"
            inputMode="decimal"
          />
        </div>
      )}
      {form.deduction_type === "lease_up" && (
        <LeaseUpCalculator
          value={form.lease_up}
          onChange={(next) => setForm((prev) => ({ ...prev, lease_up: next }))}
          pgiAnnual={PGI}
          opexAnnual={EXP}
          stabilizedVacancyPct={vacancyPct}
          defaultDiscountAnnualPct={(appliedCap || 0) * 100}
          totalBuildingSF={totalBuildingSF}
        />
      )}
    </div>
  );
}
function ValuePanel({ deductionType, indicated, manualAmount, leaseUp }) {
  const activeDeduction =
    deductionType === "manual" ? manualAmount : leaseUp || 0;

  const rawIndicated = Number(indicated) || 0;
  const rawAdjusted = rawIndicated - (activeDeduction || 0);
  const adjusted = Math.max(0, rawAdjusted);

  const showAdjustment =
    deductionType !== "none" && activeDeduction !== 0;

  const fmt = (v) =>
    isFinite(v) ? `$${Math.round(v).toLocaleString()}` : "—";

  if (!showAdjustment) {
    // No deduction / zero adjustment → show plain indicated value
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-xs text-slate-600">
          Indicated Value (NOI ÷ Applied Cap)
        </div>
        <div className="text-2xl font-semibold">
          {fmt(rawIndicated)}
        </div>
      </div>
    );
  }

  // Any non-zero deduction (positive = subtract, negative = add) → show adjusted value
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
      <div className="text-xs text-emerald-700">
        Adjusted Value (Indicated − Deduction)
      </div>
      <div className="text-2xl font-semibold">
        {fmt(adjusted)}
      </div>
    </div>
  );
}

function Notes({ value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">Notes</label>
      <textarea
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2"
      />
    </div>
  );
}